import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../src/components/ui/card';
import { Button } from '../../../../src/components/ui/button';
import { Badge } from '../../../../src/components/ui/badge';
import { Input } from '../../../../src/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../src/components/ui/tabs';
import {
  BookOpen,
  Search,
  Crown,
  Users,
  Shield,
  Zap,
  Building,
  Vote,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  HelpCircle,
  X,
} from 'lucide-react';

interface GlossaryTerm {
  id: string;
  term: string;
  category: 'roles' | 'processes' | 'concepts' | 'comparison';
  shortDefinition: string;
  fullDefinition: string;
  examples: string[];
  relatedTerms: string[];
  icon: React.ComponentType<any>;
  traditionalEquivalent?: string;
}

const glossaryTerms: GlossaryTerm[] = [
  {
    id: 'community-elect',
    term: 'Community Elect',
    category: 'roles',
    shortDefinition: 'The final decision maker who stays above day-to-day politics',
    fullDefinition:
      "Think of this as your community's \"wise judge\" - they don't get involved in daily decisions, but when there's a serious disagreement or emergency, they step in to make the final call. They're elected by the community to be fair and impartial.",
    examples: [
      "If directors can't agree on a major budget item, the Community Elect makes the final decision",
      'During emergencies, the Community Elect can authorize immediate actions',
      "Residents can petition the Community Elect to override a director's decision",
    ],
    relatedTerms: ['community-director', 'override-authority', 'community-subpanel'],
    icon: Crown,
    traditionalEquivalent: 'Board President (but stays neutral instead of being political)',
  },
  {
    id: 'community-director',
    term: 'Community Director',
    category: 'roles',
    shortDefinition: 'The people who handle day-to-day community decisions and operations',
    fullDefinition:
      'These are your community\'s "working managers" - usually 1-3 people who make the everyday decisions that keep your community running. They propose solutions, manage budgets, and handle resident concerns.',
    examples: [
      'Community Director for Operations handles maintenance and repairs',
      'Community Director for Resources manages the budget and contracts',
      'Community Director for Community organizes events and communications',
    ],
    relatedTerms: ['community-elect', 'community-subpanel', 'department-subpanel'],
    icon: Users,
    traditionalEquivalent: 'Board Directors/Committee Chairs (but more clearly defined roles)',
  },
  {
    id: 'community-subpanel',
    term: 'Community Subpanel',
    category: 'concepts',
    shortDefinition: "Your community's main leadership team",
    fullDefinition:
      "This is the core group that governs your community - one Community Elect and up to three Community Directors. It's like having a small, efficient city council that actually gets things done quickly.",
    examples: [
      'Makes decisions about community-wide policies',
      'Manages the overall budget and major expenditures',
      'Represents the community in legal and business matters',
    ],
    relatedTerms: ['community-elect', 'community-director', 'department-subpanel'],
    icon: Building,
    traditionalEquivalent: 'HOA Board (but smaller and more efficient)',
  },
  {
    id: 'department-subpanel',
    term: 'Department Subpanel',
    category: 'concepts',
    shortDefinition: 'Specialized teams for specific areas like security or maintenance',
    fullDefinition:
      'These are smaller teams that focus on specific aspects of community life - like security, landscaping, or social activities. Each department has the same structure: one leader (Department Elect) and helpers (Department Directors).',
    examples: [
      'Security Department manages safety and access control',
      'Maintenance Department handles repairs and upkeep',
      'Social Department organizes community events',
    ],
    relatedTerms: ['community-subpanel', 'community-elect', 'community-director'],
    icon: Shield,
    traditionalEquivalent: 'Committees (but with clear leadership and decision-making power)',
  },
  {
    id: 'override-authority',
    term: 'Override Authority',
    category: 'concepts',
    shortDefinition: "The power to make final decisions when there's disagreement",
    fullDefinition:
      'This is like having a "tie-breaker" or "appeals court" for your community. When directors disagree or residents are unhappy with a decision, someone with override authority can step in and make the final call.',
    examples: [
      'Community votes to ask Community Elect to override a budget decision',
      'Emergency situations where normal voting would be too slow',
      'Resolving conflicts between different departments',
    ],
    relatedTerms: ['community-elect', 'community-override', 'emergency-bypass'],
    icon: Zap,
    traditionalEquivalent: 'Board President authority (but used more rarely and fairly)',
  },
  {
    id: 'community-override',
    term: 'Community Override',
    category: 'processes',
    shortDefinition: "Residents' power to change or reverse leadership decisions",
    fullDefinition:
      "This gives YOU, as a resident, real power. If you don't like what your leadership is doing, you can organize other residents to override their decisions or even remove them from office through digital voting.",
    examples: [
      'Petition to reverse a controversial rule change',
      'Vote to remove an underperforming Community Director',
      'Request Community Elect review of a major spending decision',
    ],
    relatedTerms: ['digital-voting', 'override-authority', 'voting-eligibility'],
    icon: Vote,
    traditionalEquivalent: 'Resident petitions/recalls (but much easier and faster)',
  },
  {
    id: 'digital-governance',
    term: 'Digital Governance',
    category: 'concepts',
    shortDefinition: 'Using technology to make community decisions faster and more transparent',
    fullDefinition:
      'Instead of monthly meetings and paper ballots, everything happens online in real-time. You can vote on your phone, see exactly how decisions are made, and track what your leaders are doing 24/7.',
    examples: [
      'Vote on community issues from your smartphone',
      'See real-time budget spending and decision tracking',
      'Get instant notifications about issues affecting your property',
    ],
    relatedTerms: ['community-override', 'voting-eligibility', 'transparency'],
    icon: Zap,
    traditionalEquivalent: 'Traditional HOA meetings (but online and much more efficient)',
  },
  {
    id: 'voting-eligibility',
    term: 'Voting Eligibility',
    category: 'processes',
    shortDefinition: 'Who can vote on what decisions in your community',
    fullDefinition:
      'Clear rules about which residents can vote on which issues, based on property ownership, residency status, and the type of decision being made. No more confusion about who has a voice.',
    examples: [
      'Property owners vote on budget and major policy changes',
      'All residents vote on community events and amenities',
      'Affected properties vote on local improvements like street repairs',
    ],
    relatedTerms: ['digital-voting', 'community-override', 'stakeholder-verification'],
    icon: CheckCircle,
    traditionalEquivalent: 'Voting rights rules (but clearer and automatically enforced)',
  },
  {
    id: 'emergency-bypass',
    term: 'Emergency Bypass',
    category: 'processes',
    shortDefinition: 'Fast-track decision making for urgent safety or infrastructure issues',
    fullDefinition:
      "When there's an emergency like a water main break or security threat, leaders can make immediate decisions without waiting for normal voting processes. But these decisions are still tracked and reviewed.",
    examples: [
      'Road washout blocking emergency access gets immediate repair approval',
      'Security breach triggers immediate protocol changes',
      'Water system failure gets emergency contractor authorization',
    ],
    relatedTerms: ['override-authority', 'community-elect', 'community-oversight'],
    icon: AlertTriangle,
    traditionalEquivalent: 'Emergency board powers (but with better oversight and transparency)',
  },
  {
    id: 'legacy-hoa',
    term: 'Legacy HOA',
    category: 'comparison',
    shortDefinition: "Traditional homeowner association governance that's slow and frustrating",
    fullDefinition:
      'The old way of running communities: monthly meetings, unclear roles, political infighting, and residents who feel powerless. Decisions take months, transparency is limited, and accountability is poor.',
    examples: [
      'Board meetings where nothing gets decided',
      'Months of delays for simple maintenance approvals',
      'Residents frustrated by lack of transparency in spending',
    ],
    relatedTerms: ['digital-governance', 'community-subpanel', 'transparency'],
    icon: AlertTriangle,
    traditionalEquivalent: 'What most communities are stuck with now',
  },
];

interface GovernanceGlossaryProps {
  isModal?: boolean;
  onClose?: () => void;
  initialTerm?: string;
}

export function GovernanceGlossary({
  isModal = false,
  onClose,
  initialTerm,
}: GovernanceGlossaryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTerm, setSelectedTerm] = useState<string>(initialTerm || '');

  const filteredTerms = useMemo(() => {
    let terms = glossaryTerms;

    // Filter by category
    if (selectedCategory !== 'all') {
      terms = terms.filter((term) => term.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      terms = terms.filter(
        (term) =>
          term.term.toLowerCase().includes(query) ||
          term.shortDefinition.toLowerCase().includes(query) ||
          term.fullDefinition.toLowerCase().includes(query),
      );
    }

    return terms;
  }, [searchQuery, selectedCategory]);

  const categories = [
    { id: 'all', label: 'All Terms', count: glossaryTerms.length },
    {
      id: 'roles',
      label: 'Leadership Roles',
      count: glossaryTerms.filter((t) => t.category === 'roles').length,
    },
    {
      id: 'concepts',
      label: 'Key Concepts',
      count: glossaryTerms.filter((t) => t.category === 'concepts').length,
    },
    {
      id: 'processes',
      label: 'How It Works',
      count: glossaryTerms.filter((t) => t.category === 'processes').length,
    },
    {
      id: 'comparison',
      label: 'vs Traditional',
      count: glossaryTerms.filter((t) => t.category === 'comparison').length,
    },
  ];

  const selectedTermData = selectedTerm ? glossaryTerms.find((t) => t.id === selectedTerm) : null;

  const containerClass = isModal
    ? 'max-h-[80vh] overflow-y-auto'
    : 'min-h-screen bg-linear-to-br from-blue-50 to-purple-50';

  const contentClass = isModal ? 'p-6' : 'container mx-auto px-4 py-12';

  return (
    <div className={containerClass}>
      <div className={contentClass}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Governance Glossary</h1>
              <p className="text-muted-foreground">
                Plain-English explanations of community governance terms
              </p>
            </div>
          </div>
          {isModal && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Quick Stats */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{glossaryTerms.length}</div>
                <div className="text-sm text-muted-foreground">Terms Explained</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 mb-2">4</div>
                <div className="text-sm text-muted-foreground">Categories Covered</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
                <div className="text-sm text-muted-foreground">Plain English</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Search and Categories */}
          <div className="lg:col-span-4">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Find Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(e.target.value)
                    }
                    className="pl-9"
                  />
                </div>

                {/* Category Filters */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">Categories</div>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-blue-100 text-blue-900 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{category.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {category.count}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Terms List and Detail */}
          <div className="lg:col-span-8">
            {selectedTermData ? (
              /* Term Detail View */
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <selectedTermData.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{selectedTermData.term}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="capitalize">
                            {selectedTermData.category.replace('-', ' ')}
                          </Badge>
                          {selectedTermData.traditionalEquivalent && (
                            <Badge variant="outline" className="text-xs">
                              vs: {selectedTermData.traditionalEquivalent.split('(')[0].trim()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTerm('')}>
                      <ArrowRight className="w-4 h-4 rotate-180" />
                      Back to List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Quick Definition */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800 mb-2">Quick Definition</div>
                    <div className="text-blue-900">{selectedTermData.shortDefinition}</div>
                  </div>

                  {/* Full Explanation */}
                  <div>
                    <div className="text-lg font-semibold mb-3">Detailed Explanation</div>
                    <div className="text-muted-foreground leading-relaxed">
                      {selectedTermData.fullDefinition}
                    </div>
                  </div>

                  {/* Examples */}
                  <div>
                    <div className="text-lg font-semibold mb-3">Real Examples</div>
                    <div className="space-y-2">
                      {selectedTermData.examples.map((example, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <div className="text-sm text-green-800">{example}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Traditional Equivalent */}
                  {selectedTermData.traditionalEquivalent && (
                    <div>
                      <div className="text-lg font-semibold mb-3">Compared to Traditional HOAs</div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="text-amber-900">
                          {selectedTermData.traditionalEquivalent}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Related Terms */}
                  {selectedTermData.relatedTerms.length > 0 && (
                    <div>
                      <div className="text-lg font-semibold mb-3">Related Terms</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedTermData.relatedTerms.map((termId) => {
                          const relatedTerm = glossaryTerms.find((t) => t.id === termId);
                          return relatedTerm ? (
                            <Button
                              key={termId}
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedTerm(termId)}
                              className="text-sm"
                            >
                              {relatedTerm.term}
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Terms List View */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedCategory === 'all'
                        ? 'All Terms'
                        : categories.find((c) => c.id === selectedCategory)?.label}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                </div>

                <div className="grid gap-4">
                  {filteredTerms.map((term) => (
                    <Card
                      key={term.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedTerm(term.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                              <term.icon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{term.term}</h3>
                                <Badge className="text-xs capitalize">
                                  {term.category.replace('-', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {term.shortDefinition}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground ml-2 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {filteredTerms.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-12">
                      <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">No terms found</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Try adjusting your search or selecting a different category.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                        }}
                      >
                        Clear Filters
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
